import { LegalPage, LegalSection } from "@/components/layout/LegalPage";
import { useCompanySettings } from "@/context/SettingsContext";

export default function PrivacyPage() {
  const { settings } = useCompanySettings();
  const co = settings.company_name;
  return (
    <LegalPage title="Privacy Policy" subtitle="Last updated: July 2026" testId="privacy-page">
      <LegalSection title="1. Introduction">
        <p>
          {co} (“we”, “us”, “our”) respects your privacy. This Privacy Policy explains what information we collect,
          why we collect it, how we use and protect it, and the choices you have. By using our Platform you consent to the
          practices described in this policy.
        </p>
      </LegalSection>
      <LegalSection title="2. Information We Collect">
        <ul className="list-disc list-inside space-y-1">
          <li><span className="font-medium text-foreground">Mobile number</span> — collected when you apply for a job, build a resume or log in. It is your unique identity on the Platform.</li>
          <li><span className="font-medium text-foreground">Name and application details</span> — collected when you apply to jobs.</li>
          <li><span className="font-medium text-foreground">Resume information</span> — education, experience, skills, languages and contact details you choose to enter in the Resume Builder.</li>
          <li><span className="font-medium text-foreground">Recruitment information</span> — notes and details recorded by our recruiters after speaking with you, used solely to process your applications.</li>
          <li><span className="font-medium text-foreground">Usage information</span> — how you arrived at the Platform (e.g. Instagram, Facebook, referral) so we can improve our reach.</li>
        </ul>
      </LegalSection>
      <LegalSection title="3. OTP Authentication">
        <p>
          We use One-Time Passwords (OTPs) sent to your mobile number to verify your identity. OTPs are short-lived,
          single-use and never shared with third parties for marketing. We may keep minimal OTP logs for security and
          fraud prevention.
        </p>
      </LegalSection>
      <LegalSection title="4. Resume Storage">
        <p>
          Resumes you save are stored securely against your account and remain accessible to you at any time. Your resume
          and profile details are visible to {co} recruiters and administrators only for the purpose of processing your job
          applications. We do not sell your resume or personal data to any third party.
        </p>
      </LegalSection>
      <LegalSection title="5. How We Use Your Information">
        <ul className="list-disc list-inside space-y-1">
          <li>To process and track your job applications;</li>
          <li>To connect you with a recruiter who assists you personally;</li>
          <li>To contact you about your applications via phone, SMS or WhatsApp;</li>
          <li>To provide and improve the Resume Builder and other services;</li>
          <li>To maintain security, prevent fraud and comply with legal obligations.</li>
        </ul>
      </LegalSection>
      <LegalSection title="6. Cookies & Local Storage">
        <p>
          We use browser local storage to keep you logged in, remember your unsaved resume drafts and remember how you
          discovered us. We do not use third-party advertising cookies. See our Cookie Policy for details.
        </p>
      </LegalSection>
      <LegalSection title="7. Data Sharing">
        <p>
          We share your candidate details with prospective employers only as part of processing your job application.
          We may disclose information if required by law or to protect the rights, property or safety of {co}, our users
          or the public.
        </p>
      </LegalSection>
      <LegalSection title="8. Data Retention & Security">
        <p>
          We retain your data for as long as your account is active or as needed to provide services and meet legal
          requirements. We use industry-standard safeguards including encrypted transport (HTTPS), role-based access
          control and audit logging of staff actions.
        </p>
      </LegalSection>
      <LegalSection title="9. Your Rights">
        <ul className="list-disc list-inside space-y-1">
          <li>Access and update your resume and personal details at any time from your portal;</li>
          <li>Request correction of inaccurate information;</li>
          <li>Request deletion of your account and associated data;</li>
          <li>Withdraw consent for communications (except essential application updates).</li>
        </ul>
        <p>To exercise these rights, contact us at {settings.support_email || "our support email"}.</p>
      </LegalSection>
      <LegalSection title="10. Children's Privacy">
        <p>The Platform is intended for users aged 18 and above. We do not knowingly collect data from minors.</p>
      </LegalSection>
      <LegalSection title="11. Changes to This Policy">
        <p>We may update this policy from time to time. Material changes will be highlighted on this page with a revised “Last updated” date.</p>
      </LegalSection>
      <LegalSection title="12. Contact Us">
        <p>
          Questions about this Privacy Policy? Reach us at {settings.support_email || "our support email"}
          {settings.office_address ? `, or write to: ${settings.office_address}` : ""}.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
